import type { DemoAppointment } from "@emr/contracts";
import { useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useOptionalDemoScheduling } from "../../demo/DemoSchedulingProvider";
import { scheduleMessagesSearch } from "../../lib/scheduleSearch";
import { appointmentRowFacts, appointmentRowState } from "./appointmentRowState";
import { appointmentStatusLabel } from "./appointmentStatus";
import { opensBookingReview } from "./BookingReviewDialog";
import { IntakeReviewPill } from "./IntakeReviewPill";

/**
 * Today's "Expected today": the clinic's bookings for its own date that have not arrived. Each
 * row carries the booking's one pill and one action (appointmentRowState): "Review intake" opens
 * the questionnaire review in place when the page provides `onReviewIntake`, and "Review booking"
 * opens a returning patient's booking review in place (PA-10: "Likely match", "No chart
 * found"...) when the page provides `onReviewBooking`. A text or email that needs a person
 * ("Text not delivered", "Check phone number"; PA-7), and a quiet "Texts stopped" pill, open the
 * booking's texts and emails on Schedule (`/schedule?appointment=<id>&panel=messages`). Anything
 * else, and "Open in Schedule", go to Schedule, where check-in happens.
 *
 * PA-9 (lead decision L8): a booking the patient confirmed from their manage link reads
 * "Confirmed by patient" under the name, beside its pill when it has one. Today's bookings the
 * patient cancelled follow the expected rows as muted "Cancelled by patient" rows with "Open in
 * Schedule" and no pill; they are not counted. Bookings staff cancelled are not listed.
 */
export function TodayExpectedAppointments({
  search,
  legacyCount,
  children,
  onReviewIntake,
  onReviewBooking,
}: {
  readonly search: string;
  readonly legacyCount: number;
  readonly children: ReactNode;
  readonly onReviewIntake?: ((appointmentId: string) => void) | undefined;
  readonly onReviewBooking?: ((appointmentId: string) => void) | undefined;
}) {
  const scheduling = useOptionalDemoScheduling();
  const navigate = useNavigate();
  const state = scheduling?.state;
  const timeZone = state?.configuration.timeZone ?? "America/Toronto";
  const day = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const today = day.format(new Date());
  const todays = (state?.appointments ?? [])
    .filter(
      (appointment) =>
        day.format(new Date(appointment.startAt)) === today &&
        `${appointment.patient.displayName} ${appointment.reason}`
          .toLocaleLowerCase()
          .includes(search.trim().toLocaleLowerCase()),
    )
    .sort((left, right) => left.startAt.localeCompare(right.startAt));
  const appointments = todays.filter(
    (appointment) =>
      ["booked", "confirmed"].includes(appointment.status) && !appointment.encounterId,
  );
  const cancelledByPatient = todays.filter(
    (appointment) =>
      appointment.status === "cancelled" && appointment.patientChange?.kind === "cancelled",
  );
  if (appointments.length + cancelledByPatient.length + legacyCount === 0 && !scheduling?.error)
    return null;
  const openSchedule = () => void navigate({ to: "/schedule" });
  const openBooking = (appointment: DemoAppointment) =>
    void navigate({ to: "/schedule", search: { appointment: appointment.id } });
  const time = (appointment: DemoAppointment) => (
    <time dateTime={appointment.startAt}>
      {new Intl.DateTimeFormat("en-CA", {
        timeZone,
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date(appointment.startAt))}
    </time>
  );
  return (
    <section className="tb-queue-section" aria-label="Expected today">
      <header>
        <h2 className="tb-eyebrow">
          Expected today <span>{appointments.length + legacyCount}</span>
        </h2>
        <p>Not checked in yet</p>
      </header>
      {scheduling?.error ? (
        <p role="alert">Bookings could not be refreshed. Open Schedule to retry.</p>
      ) : null}
      <ul className="tb-patients">
        {children}
        {appointments.map((appointment) => {
          const name = appointment.patient.displayName;
          const { action, pill } = appointmentRowState(appointmentRowFacts(appointment));
          const reviewIntake =
            onReviewIntake === undefined ? openSchedule : () => onReviewIntake(appointment.id);
          // A booking whose questionnaire is in is never review_booking (its chart is linked in
          // Review intake), so Review booking opens only a booking without answers.
          const reviewBooking =
            onReviewBooking !== undefined && opensBookingReview(appointment)
              ? () => onReviewBooking(appointment.id)
              : openSchedule;
          const reviewMessages = () =>
            void navigate({ to: "/schedule", search: scheduleMessagesSearch(appointment.id) });
          const open = (kind: "booking" | "intake" | "code" | "message" | undefined) =>
            kind === "intake"
              ? reviewIntake
              : kind === "booking"
                ? reviewBooking
                : kind === "message"
                  ? reviewMessages
                  : openSchedule;
          // "Confirmed by patient" never hides the pill: it sits above it.
          const confirmedByPatient =
            appointment.patientChange?.kind === "confirmed"
              ? appointmentStatusLabel(appointment)
              : null;
          return (
            <li className="tb-patient-row" key={appointment.id}>
              <div className="tb-patient-header tb-expected-header">
                {time(appointment)}
                <div className="tb-person">
                  <strong>{name}</strong>
                  {pill === null ? (
                    <small>{confirmedByPatient ?? "Awaiting check-in"}</small>
                  ) : (
                    <>
                      {confirmedByPatient === null ? null : <small>{confirmedByPatient}</small>}
                      <IntakeReviewPill
                        pill={pill}
                        patientName={name}
                        surface="today"
                        onOpen={open(pill.kind)}
                      />
                    </>
                  )}
                </div>
                <span className="tb-reason">{appointment.reason}</span>
                <button
                  type="button"
                  className="tb-button tb-row-action"
                  aria-label={`${action?.label ?? "Open in Schedule"} for ${name}`}
                  onClick={
                    action?.kind === "review_intake"
                      ? reviewIntake
                      : action?.kind === "review_booking"
                        ? reviewBooking
                        : action?.kind === "review_messages"
                          ? reviewMessages
                          : openSchedule
                  }
                >
                  {action?.label ?? "Open in Schedule"}
                </button>
              </div>
            </li>
          );
        })}
        {cancelledByPatient.map((appointment) => (
          <li className="tb-patient-row is-cancelled" key={appointment.id}>
            <div className="tb-patient-header tb-expected-header">
              {time(appointment)}
              <div className="tb-person">
                <strong>{appointment.patient.displayName}</strong>
                <small>{appointmentStatusLabel(appointment)}</small>
              </div>
              <span className="tb-reason">{appointment.reason}</span>
              <button
                type="button"
                className="tb-button tb-row-action"
                aria-label={`Open in Schedule for ${appointment.patient.displayName}`}
                onClick={() => openBooking(appointment)}
              >
                Open in Schedule
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
